/**
 * ЯДРО i18n: определение языка, хранение, подписка, React-хуки.
 *
 * УСТРОЙСТВО:
 *   - `Lang` — union поддерживаемых языков;
 *   - `LANGS` — метаданные для UI (название на самом языке, флаг);
 *   - `detectLang()` — по `navigator.languages`, с fallback на `en`;
 *   - `readLang()` / `writeLang()` — localStorage `alg.lang`;
 *   - `applyLang()` — пишет, ставит `document.documentElement.lang`,
 *     уведомляет подписчиков. БЕЗ `location.reload()`;
 *   - `useLang()` — React-хук, возвращает `{ lang, setLang }`,
 *     перерисовывает подписанные компоненты при смене;
 *   - `useT()` — хук, возвращает функцию `t(key)` для текущего языка.
 *
 * Подписка реализована через простой набор слушателей + `useSyncExternalStore`,
 * чтобы не тянуть внешние state-менеджеры.
 */

import { useSyncExternalStore } from "react";
import { ru, en, es, ptBR, type Messages } from "./messages";

export type Lang = "ru" | "en" | "es" | "pt-BR";

export interface LangMeta {
  code: Lang;
  label: string;
  flag: string;
}

export const LANGS: LangMeta[] = [
  { code: "ru", label: "Русский", flag: "/flags/ru.svg" },
  { code: "en", label: "English", flag: "/flags/en.svg" },
  { code: "es", label: "Español", flag: "/flags/es.svg" },
  { code: "pt-BR", label: "Português (BR)", flag: "/flags/pt-BR.svg" },
];

const builtin: Record<Lang, Messages> = {
  ru,
  en,
  es,
  "pt-BR": ptBR,
};

/**
 * ДИНАМИЧЕСКИЕ ПЕРЕВОДЫ (задел на будущее, Антон 14.09 19:44).
 *
 * Встроенные словари — база, которая всегда есть в сборке. Поверх неё можно
 * наложить словарь, пришедший извне (файл, сервер, панель управления): он
 * перекрывает только те ключи, которые в нём есть, остальное остаётся
 * встроенным. Так тексты можно править без пересборки приложения.
 *
 * Порядок поиска перевода: наложенный -> встроенный -> английский -> ключ.
 * Английский как запасной осмысленнее пустоты: увидев "room.leaveMenu"
 * вместо текста, пользователь не поймёт ничего, а английскую фразу поймёт.
 */
const overrides: Partial<Record<Lang, Partial<Messages>>> = {};

/** Наложить внешний словарь (частичный) и перерисовать подписчиков. */
export function applyTranslations(lang: Lang, dict: Partial<Messages>): void {
  overrides[lang] = { ...(overrides[lang] ?? {}), ...dict };
  notify();
}

/** Убрать наложенные переводы (вернуться к встроенным). */
export function resetTranslations(lang?: Lang): void {
  if (lang) delete overrides[lang];
  else for (const k of Object.keys(overrides) as Lang[]) delete overrides[k];
  notify();
}

/** Перевод одного ключа с полной цепочкой запасных вариантов. */
export function translate(lang: Lang, key: keyof Messages): string {
  return (
    overrides[lang]?.[key] ??
    builtin[lang][key] ??
    overrides.en?.[key] ??
    builtin.en[key] ??
    String(key)
  );
}

/* ------------------------------------------------------------------------
 * ФОРМАТИРОВАНИЕ: tf — перевод + подстановка {name}/{count} и т.п.
 * --------------------------------------------------------------------- */

/** Значения для подстановки в шаблон. Числа приводятся к строке. */
export type TFVars = Record<string, string | number>;

/**
 * Подставить {name}/{count}/… в шаблон. Неизвестные плейсхолдеры
 * (которых нет в vars) остаются как есть — чтобы опечатка в ключе не
 * выбросила исключение и не сломала рендер, а спокойно подсветила место.
 */
function substitute(template: string, vars?: TFVars): string {
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (whole, name: string) =>
    Object.prototype.hasOwnProperty.call(vars, name)
      ? String(vars[name])
      : whole,
  );
}

/**
 * Форматированный перевод ВНЕ React (для модулей matrix/*): берёт текущий
 * язык из снапшота и подставляет переменные. Аналог `tr`, но с vars.
 */
export function tf(key: keyof Messages, vars?: TFVars): string {
  return substitute(translate(getLangSnapshot(), key), vars);
}

/* ------------------------------------------------------------------------
 * ОПРЕДЕЛЕНИЕ ЯЗЫКА
 * --------------------------------------------------------------------- */

/**
 * Определить язык по `navigator.languages`.
 * `pt-*` → `pt-BR`, `ru-*` → `ru`, `es-*` → `es`, иначе `en`.
 */
export function detectLang(): Lang {
  if (typeof navigator === "undefined" || !navigator.languages) return "en";
  for (const tag of navigator.languages) {
    const lower = tag.toLowerCase();
    if (lower.startsWith("pt")) return "pt-BR";
    if (lower.startsWith("ru")) return "ru";
    if (lower.startsWith("es")) return "es";
    if (lower.startsWith("en")) return "en";
  }
  return "en";
}

/* ------------------------------------------------------------------------
 * ХРАНЕНИЕ
 * --------------------------------------------------------------------- */

const STORAGE_KEY = "alg.lang";

/** Текущий язык в памяти (инициализируется из localStorage). */
let currentLang: Lang = readLang();

/** Прочитать язык из localStorage; если пусто — detectLang(). */
export function readLang(): Lang {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === "ru" || v === "en" || v === "es" || v === "pt-BR") return v;
  } catch {
    /* localStorage может быть недоступен */
  }
  return detectLang();
}

/** Записать язык в localStorage. */
export function writeLang(lang: Lang): void {
  try {
    localStorage.setItem(STORAGE_KEY, lang);
  } catch {
    /* ignore */
  }
}

/* ------------------------------------------------------------------------
 * ПОДПИСКА И ПРИМЕНЕНИЕ
 * --------------------------------------------------------------------- */

type Listener = () => void;
const listeners = new Set<Listener>();

function notify(): void {
  for (const fn of listeners) fn();
}

/**
 * Применить язык: пишет в localStorage, ставит `<html lang>`,
 * уведомляет подписчиков. НЕ перезагружает страницу.
 */
export function applyLang(lang: Lang): void {
  writeLang(lang);
  setLangInternal(lang);
}

/**
 * Применить язык БЕЗ записи в localStorage — для старта приложения.
 *
 * ГРАБЛЯ (Антон, 14.09 21:19): раньше старт звал applyLang(readLang()),
 * то есть ЗАПИСЫВАЛ определённый язык в хранилище при первом же заходе.
 * У кого приложение однажды открылось русским (в т.ч. до появления
 * переключателя), в localStorage навсегда оседало "ru" — и английский
 * браузер потом ничего не менял: сохранённый выбор сильнее определения.
 * Теперь в хранилище попадает ТОЛЬКО осознанный выбор в профиле.
 */
export function initLang(): void {
  setLangInternal(readLang());
}

function setLangInternal(lang: Lang): void {
  currentLang = lang;
  if (typeof document !== "undefined") {
    document.documentElement.lang = lang;
  }
  notify();
}

/** Текущий язык (синхронный снапшот для useSyncExternalStore). */
export function getLangSnapshot(): Lang {
  return currentLang;
}

/** Подписаться на смену языка. Возвращает функцию отписки. */
export function subscribeLang(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/* ------------------------------------------------------------------------
 * REACT-ХУКИ
 * --------------------------------------------------------------------- */

/**
 * Хук: `{ lang, setLang }`. Компонент перерисовывается при смене языка.
 */
export function useLang(): { lang: Lang; setLang: (lang: Lang) => void } {
  const lang = useSyncExternalStore(subscribeLang, getLangSnapshot);
  return { lang, setLang: applyLang };
}

/**
 * Хук: функция `t(key)` для текущего языка.
 * Компонент перерисовывается при смене языка.
 */
export function useT(): (key: keyof Messages) => string {
  const lang = useSyncExternalStore(subscribeLang, getLangSnapshot);
  return (key: keyof Messages) => translate(lang, key);
}

/**
 * Перевод ВНЕ React: для модулей matrix/* (звонки, действия в комнате),
 * где хук недоступен — там нет компонента. Берёт текущий язык из снапшота.
 */
export function tr(key: keyof Messages): string {
  return translate(getLangSnapshot(), key);
}

/**
 * React-хук: функция `tf(key, vars)` для текущего языка с подстановкой.
 * Компонент перерисовывается при смене языка.
 */
export function useTF(): (key: keyof Messages, vars?: TFVars) => string {
  const lang = useSyncExternalStore(subscribeLang, getLangSnapshot);
  return (key: keyof Messages, vars?: TFVars) => substitute(translate(lang, key), vars);
}

/* ------------------------------------------------------------------------
 * ЛОКАЛЬ ДЛЯ ФОРМАТИРОВАНИЯ ДАТ И ЧИСЕЛ
 * --------------------------------------------------------------------- */

/** Код языка интерфейса → локаль для Intl (toLocaleString/toLocaleDateString). */
export function langToLocale(lang: Lang): string {
  switch (lang) {
    case "ru":
      return "ru-RU";
    case "es":
      return "es-ES";
    case "pt-BR":
      return "pt-BR";
    default:
      return "en-US";
  }
}

/** Текущая локаль из снапшота — для ВНЕ-React кода и чистых функций. */
export function currentLocale(): string {
  return langToLocale(getLangSnapshot());
}

/** React-хук: локаль текущего языка; перерисовывается при смене. */
export function useLocale(): string {
  const lang = useSyncExternalStore(subscribeLang, getLangSnapshot);
  return langToLocale(lang);
}
